import { useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import TopNav from './TopNav';
import AskEcowaterAI from '../ai/AskEcowaterAI';
import DayStarterPopup from '../DayStarterPopup';

/* Mobile-first layout rules
   ──────────────────────────
   • ≥ md (768px): sidebar is fixed on the left (16 or 64 wide), main content
     gets a matching left margin. Identical to the previous desktop UX.
   • < md      : sidebar is hidden by default. A hamburger in TopNav slides it
     in as an overlay backed by a dimmed scrim. Main content has zero left
     margin and full horizontal padding shrinks slightly so tables / cards
     don't get cut off on small screens.
*/
export default function MainLayout() {
  const { loading, isAuthenticated, mustChangePassword, user, isSuperAdmin } = useAuth();
  const location = useLocation();

  const enabledModules = new Set(user?.enabled_modules || []);
  const canUseAI = isSuperAdmin || enabledModules.has('ai_assistant');

  // Desktop collapse — unchanged behavior.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Mobile drawer state.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the drawer when the route changes on mobile.
  // Using location.pathname as the trigger; we just reset the state.
  // (No useEffect needed — simply close on first render of the new path.)

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">
        <div className="animate-pulse text-[#0A2540]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <div className="min-h-screen bg-[#FCFAF6]">
      {/* Mobile scrim — visible only when drawer open */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-sidebar-scrim"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
        />
      )}

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        key={location.pathname /* forces re-mount on route change so internal effects reset */}
      />

      <div
        className={`transition-all duration-300 ml-0 ${
          sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'
        }`}
      >
        <TopNav onMobileMenu={() => setMobileOpen(true)} />
        <main className="p-4 md:p-6" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {canUseAI && <AskEcowaterAI />}
      <DayStarterPopup />
    </div>
  );
}
